import { useState } from "react";

const CCLEAN_BASE =
  (import.meta as unknown as { env?: Record<string, string | undefined> }).env
    ?.VITE_CARPETCLEANING_ORIGIN ?? "https://carpetcleaning.co.uk";

interface Instructions {
  sortCode: string;
  accountNumber: string;
  reference: string;
  amountMinor: number;
  accountHolderName: string;
}

/** Format 6-digit UK sort code as XX-XX-XX for display + copy. */
function fmtSortCode(raw: string): string {
  const d = raw.replace(/\D/g, "");
  if (d.length !== 6) return raw;
  return `${d.slice(0, 2)}-${d.slice(2, 4)}-${d.slice(4, 6)}`;
}

function gbp(minor: number): string {
  return `£${(minor / 100).toFixed(2)}`;
}

type Props = {
  dcodel: string;
  /** Outstanding balance in £ from the invoice. Widget shows itself only
   *  when > 0 — no point minting a virtual account for a paid invoice. */
  outstanding: number;
};

/** Ask carpetcleaning.co.uk to ensure/mint a Stripe customer_balance PI
 *  for this invoice's outstanding balance, then display the virtual UK
 *  sort code / account number / reference so Andzelika can paste them
 *  to the customer. Idempotent — hitting the button twice for the same
 *  amount reuses the existing PI. */
export function BankTransferPanel({ dcodel, outstanding }: Props): JSX.Element | null {
  const [status, setStatus] = useState<"idle" | "loading" | "ready" | "error">(
    "idle",
  );
  const [instructions, setInstructions] = useState<Instructions | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [copied, setCopied] = useState<string | null>(null);

  if (outstanding <= 0.005 || !dcodel) return null;

  async function generate(): Promise<void> {
    setStatus("loading");
    setError(null);
    try {
      const res = await fetch(`${CCLEAN_BASE}/api/bank-transfer/${dcodel}`, {
        method: "POST",
      });
      const body = (await res.json()) as
        | ({ ok: true } & Instructions)
        | { error: string; hint?: string; message?: string };
      if (!res.ok || !("ok" in body)) {
        const msg =
          "hint" in body && body.hint
            ? body.hint
            : "message" in body && body.message
              ? body.message
              : "error" in body
                ? body.error
                : `HTTP ${res.status}`;
        setStatus("error");
        setError(msg);
        return;
      }
      setInstructions({
        sortCode: body.sortCode,
        accountNumber: body.accountNumber,
        reference: body.reference,
        amountMinor: body.amountMinor,
        accountHolderName: body.accountHolderName,
      });
      setStatus("ready");
    } catch (e) {
      setStatus("error");
      setError(e instanceof Error ? e.message : String(e));
    }
  }

  async function copy(label: string, value: string): Promise<void> {
    try {
      await navigator.clipboard.writeText(value);
      setCopied(label);
      setTimeout(() => setCopied((c) => (c === label ? null : c)), 1500);
    } catch {
      // Ignore — clipboard may be blocked; user can still select + Ctrl-C.
    }
  }

  return (
    <div className="mt-3 rounded-md border border-slack-aubergineLight/40 bg-ink-800/60 p-3">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="text-[12px] font-semibold uppercase tracking-[0.14em] text-ink-200">
          Bank Transfer Details
        </div>
        {status === "idle" ? (
          <button
            type="button"
            onClick={() => void generate()}
            className="rounded-md border border-slack-aubergineLight bg-slack-aubergine px-3 py-1 text-[12px] font-medium text-ink-50 hover:bg-slack-aubergineLight"
            title="Generate a virtual UK sort code + account number for the customer to pay by Faster Payments / BACS"
          >
            Generate for £{outstanding.toFixed(2)}
          </button>
        ) : status === "loading" ? (
          <span className="text-[12px] text-ink-200">Generating…</span>
        ) : status === "ready" ? (
          <button
            type="button"
            onClick={() => void generate()}
            className="text-[11px] text-ink-100 underline hover:text-slack-aubergineLight"
            title="Refresh — regenerate for the current outstanding amount"
          >
            Refresh
          </button>
        ) : (
          <button
            type="button"
            onClick={() => void generate()}
            className="rounded-md border border-red-500/60 bg-red-500/10 px-3 py-1 text-[12px] font-medium text-red-100 hover:bg-red-500/20"
          >
            Try again
          </button>
        )}
      </div>

      {status === "error" && error ? (
        <div className="mt-2 rounded-md border border-red-700 bg-red-950/30 px-3 py-2 text-[12px] text-red-200">
          {error}
        </div>
      ) : null}

      {status === "ready" && instructions ? (
        <div className="mt-3 grid gap-2 text-[13px] text-ink-50">
          <Field
            label="Account name"
            value={instructions.accountHolderName}
            copiedLabel={copied}
            onCopy={copy}
          />
          <Field
            label="Sort code"
            value={fmtSortCode(instructions.sortCode)}
            copyValue={fmtSortCode(instructions.sortCode)}
            copiedLabel={copied}
            onCopy={copy}
          />
          <Field
            label="Account number"
            value={instructions.accountNumber}
            copiedLabel={copied}
            onCopy={copy}
          />
          <Field
            label="Reference"
            value={instructions.reference}
            copiedLabel={copied}
            onCopy={copy}
          />
          <Field
            label="Amount"
            value={gbp(instructions.amountMinor)}
            copyValue={(instructions.amountMinor / 100).toFixed(2)}
            copiedLabel={copied}
            onCopy={copy}
          />
          <div className="mt-1 text-[11px] text-ink-200">
            Paste these into the customer&apos;s WhatsApp / email. Payment
            auto-links to the visit when it lands.
          </div>
        </div>
      ) : null}
    </div>
  );
}

function Field({
  label,
  value,
  copyValue,
  copiedLabel,
  onCopy,
}: {
  label: string;
  value: string;
  copyValue?: string;
  copiedLabel: string | null;
  onCopy: (label: string, value: string) => void;
}): JSX.Element {
  const c = copyValue ?? value;
  const isCopied = copiedLabel === label;
  return (
    <div className="flex items-center gap-3">
      <div className="w-28 shrink-0 text-[11px] uppercase tracking-wider text-ink-200">
        {label}
      </div>
      <div className="flex-1 truncate rounded bg-ink-900/60 px-2 py-1 font-mono tabular-nums">
        {value}
      </div>
      <button
        type="button"
        onClick={() => onCopy(label, c)}
        className="w-14 shrink-0 rounded border border-ink-500 px-2 py-1 text-[11px] text-ink-50 hover:border-slack-aubergineLight"
      >
        {isCopied ? "Copied" : "Copy"}
      </button>
    </div>
  );
}
